import { useEffect, useState } from 'react';
import type { TenantCampaignWithCapabilitiesDto } from '@signalvote/shared';
import { readReachedStep, recordReachedStep, useExpressDraft } from './expressDraft';
import { firstUnansweredStep, stepsFor, type ExpressFlowContext } from './expressProgress';
import { SEND_STEP_ORDER, type SendStepKey } from './expressSteps';
import { SEND_STEP_VIEWS } from './expressStepViews';
import { useExpressFlow } from './useExpressTextSend';

/**
 * The selected campaign's Text send: the send steps that apply to it, starting at `startAt` when
 * given, otherwise where the person left it (see `resumeStep`).
 */
export function ExpressSend({ campaign, startAt }: { campaign: TenantCampaignWithCapabilitiesDto; startAt?: SendStepKey }) {
  const flow = useExpressFlow(campaign);
  if (!flow.context) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-background px-4">
        {flow.isError ? (
          <p role="alert" className="text-sm text-destructive">
            We couldn’t load your text. Refresh to try again.
          </p>
        ) : (
          <p role="status" className="text-sm text-muted-foreground">
            Loading your text…
          </p>
        )}
      </main>
    );
  }
  return <ExpressSendSteps context={flow.context} startAt={startAt} />;
}

function ExpressSendSteps({ context, startAt }: { context: ExpressFlowContext; startAt?: SendStepKey }) {
  const steps = stepsFor(context, startAt);
  const [current, setCurrent] = useState(() =>
    startAt && steps.includes(startAt) ? startAt : resumeStep(steps, context),
  );
  const { draft, setDraft } = useExpressDraft();
  // A step can drop out once answered elsewhere, e.g. registration locking; carry on from the first open one.
  const step = steps.includes(current) ? current : firstUnansweredStep(steps, context);
  const index = steps.indexOf(step);
  const workOrderId = context.workOrder?.id;
  useEffect(() => {
    if (workOrderId) recordReachedStep(workOrderId, step);
  }, [workOrderId, step]);
  const { Component } = SEND_STEP_VIEWS[step];
  return (
    <Component
      step={step}
      steps={steps}
      draft={draft}
      setDraft={setDraft}
      campaign={context.campaign}
      onBack={index > 0 ? () => setCurrent(steps[index - 1]) : undefined}
      onNext={index < steps.length - 1 ? () => setCurrent(steps[index + 1]) : undefined}
    />
  );
}

const order = (step: SendStepKey) => SEND_STEP_ORDER.indexOf(step);

/**
 * Where a returning person picks up: the first step the Draft doesn't answer, but never past the
 * furthest step they reached on this device. Another device knows only what the Draft answers.
 */
function resumeStep(steps: readonly SendStepKey[], context: ExpressFlowContext): SendStepKey {
  const open = firstUnansweredStep(steps, context);
  const reached = context.workOrder && readReachedStep(context.workOrder.id);
  // The step reached may since have dropped out (registration locked); the next one stands in for it.
  const left = reached && steps.find((step) => order(step) >= order(reached));
  return left && order(left) < order(open) ? left : open;
}
